import endpointsConfig from './wp-cache-endpoints.json';
import cacheSeed from './wp-cache-seed.json';
import { URL_Back } from '../components/Pages/Variables';

const { endpoints } = endpointsConfig;
const memoryCache = new Map();
const inFlightRevalidations = new Map();
const DEFAULT_REVALIDATE_MS = 5 * 60 * 1000;
const CACHE_META_SUFFIX = ':meta';

function getEndpoint(id) {
  const ep = endpoints.find((e) => e.id === id);
  if (!ep) {
    throw new Error(`wpClient: unknown cache endpoint "${id}"`);
  }
  return ep;
}

function localCacheUrl(file) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/wp-cache/${file}`;
}

function getSeedData(endpointId) {
  const seed = cacheSeed?.[endpointId];
  if (!(Array.isArray(seed) || (seed && typeof seed === 'object'))) {
    return null;
  }

  try {
    return transformEndpointData(getEndpoint(endpointId), seed);
  } catch {
    return seed;
  }
}

function extractPortfolioPreviewHtml(html) {
  const source = String(html || '');
  const withoutHashtag = source.replace(
    /<div\b[^>]*class="[^"]*hashtag[^"]*"[^>]*>[\s\S]*?<\/div>/i,
    ''
  );
  const firstParagraph = withoutHashtag.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
  return firstParagraph ? `<p>${firstParagraph[1]}</p>` : '';
}

function normalizeWpImageUrl(url) {
  const source = String(url || '');
  if (!source) {
    return '';
  }

  return source.replace(/-\d+x\d+(?=\.[a-zA-Z0-9]+(?:[?#].*)?$)/, '');
}

function createPortfolioListItem(item) {
  const featuredImage = normalizeWpImageUrl(
    item?._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
      item?.x_featured_media_large ||
      ''
  );

  return {
    id: item.id,
    slug: item.slug,
    date: item.date,
    title: item.title,
    excerpt: item.excerpt,
    categoriesportfolio: item.categoriesportfolio || [],
    techo_choice_content: Array.isArray(item.techo_choice_content)
      ? item.techo_choice_content
      : [],
    image_caroussel_content: Array.isArray(item.image_caroussel_content)
      ? item.image_caroussel_content.slice(0, 3).map((imageItem) => ({
          ...imageItem,
          src: normalizeWpImageUrl(imageItem?.src),
        }))
      : [],
    x_featured_media_large: normalizeWpImageUrl(item.x_featured_media_large) || featuredImage,
    featured_image_url: featuredImage,
    content: {
      rendered: extractPortfolioPreviewHtml(item?.content?.rendered),
    },
    _embedded: featuredImage
      ? { 'wp:featuredmedia': [{ source_url: featuredImage }] }
      : undefined,
    is_lightweight: true,
  };
}

function normalizePortfolioItem(item) {
  if (!item || typeof item !== 'object') {
    return item;
  }

  const normalizedFeaturedImage = normalizeWpImageUrl(
    item?._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
      item?.x_featured_media_large ||
      item?.featured_image_url ||
      ''
  );

  return {
    ...item,
    image_caroussel_content: Array.isArray(item.image_caroussel_content)
      ? item.image_caroussel_content.map((imageItem) => ({
          ...imageItem,
          src: normalizeWpImageUrl(imageItem?.src),
        }))
      : item.image_caroussel_content,
    x_featured_media_large:
      normalizeWpImageUrl(item.x_featured_media_large) || normalizedFeaturedImage,
    featured_image_url:
      normalizeWpImageUrl(item.featured_image_url) || normalizedFeaturedImage,
    _embedded:
      item?._embedded?.['wp:featuredmedia']?.[0]?.source_url
        ? {
            ...item._embedded,
            'wp:featuredmedia': item._embedded['wp:featuredmedia'].map((mediaItem, index) =>
              index === 0
                ? {
                    ...mediaItem,
                    source_url:
                      normalizeWpImageUrl(mediaItem?.source_url) || mediaItem?.source_url,
                  }
                : mediaItem
            ),
          }
        : item._embedded,
  };
}

function transformEndpointData(endpoint, data) {
  if (endpoint.id === 'portfolioAll') {
    return Array.isArray(data) ? data.map(normalizePortfolioItem) : [];
  }

  if (endpoint.transform === 'portfolioList') {
    return Array.isArray(data) ? data.map(createPortfolioListItem) : [];
  }
  return data;
}

Object.keys(cacheSeed || {}).forEach((endpointId) => {
  const seedData = getSeedData(endpointId);
  if (seedData != null) {
    setMemoryCache(endpointId, seedData, 0);
  }
});

function readStoredTimestamp(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}${CACHE_META_SUFFIX}`);
    const meta = raw ? JSON.parse(raw) : null;
    return typeof meta?.updatedAt === 'number' ? meta.updatedAt : 0;
  } catch {
    return 0;
  }
}

function writeStoredTimestamp(storageKey, updatedAt) {
  try {
    localStorage.setItem(
      `${storageKey}${CACHE_META_SUFFIX}`,
      JSON.stringify({ updatedAt })
    );
  } catch {
    /* ignore localStorage errors */
  }
}

function readStoredData(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }
    return {
      data: JSON.parse(stored),
      updatedAt: readStoredTimestamp(storageKey),
    };
  } catch {
    return null;
  }
}

function writeStoredData(storageKey, data, persistToStorage = true) {
  const updatedAt = Date.now();

  if (persistToStorage) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      /* ignore localStorage errors */
    }
  }

  if (persistToStorage) {
    writeStoredTimestamp(storageKey, updatedAt);
  }
  return updatedAt;
}

function clearStoredData(storageKey) {
  try {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(`${storageKey}${CACHE_META_SUFFIX}`);
  } catch {
    /* ignore localStorage errors */
  }
}

function setMemoryCache(endpointId, data, updatedAt = Date.now()) {
  memoryCache.set(endpointId, { data, updatedAt });
}

function getMemoryCache(endpointId) {
  return memoryCache.get(endpointId) || null;
}

function shouldRevalidate(updatedAt, revalidateMs) {
  if (!updatedAt) {
    return true;
  }
  return Date.now() - updatedAt >= revalidateMs;
}

async function fetchLocalStaticCache(file) {
  const res = await fetch(localCacheUrl(file), { cache: 'force-cache' });
  if (!res.ok) {
    throw new Error(`wpClient: static cache "${file}" -> ${res.status}`);
  }
  return res.json();
}

async function fetchPaginatedRemote(paginatedBase) {
  let all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const sep = paginatedBase.includes('?') ? '&' : '?';
    const url = `${URL_Back}/wp-json/${paginatedBase}${sep}page=${page}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      throw new Error(`wpClient: ${url} -> ${res.status}`);
    }
    const chunk = await res.json();
    if (!Array.isArray(chunk)) {
      break;
    }
    all = all.concat(chunk);
    totalPages = parseInt(res.headers.get('X-WP-TotalPages') || String(page), 10);
    page += 1;
  } while (page <= totalPages);
  return all;
}

async function runRemote(endpoint) {
  if (endpoint.sourceEndpointId) {
    const sourceEndpoint = getEndpoint(endpoint.sourceEndpointId);
    const sourceData = await runRemote(sourceEndpoint);
    return transformEndpointData(endpoint, sourceData);
  }

  if (endpoint.paginatedBase) {
    const data = await fetchPaginatedRemote(endpoint.paginatedBase);
    return transformEndpointData(endpoint, data);
  }

  const url = `${URL_Back}/wp-json/${endpoint.path}`;
  const res = await fetch(url, { cache: 'no-store' });

  if (!res.ok) {
    throw new Error(`wpClient: ${url} -> ${res.status}`);
  }

  const data = await res.json();
  return transformEndpointData(endpoint, data);
}

function queueRevalidation(endpointId, endpoint, storageKey, onRevalidated) {
  let request = inFlightRevalidations.get(endpointId);

  if (!request) {
    request = runRemote(endpoint)
      .then((data) => {
        const updatedAt = writeStoredData(
          storageKey,
          data,
          endpoint.persistToStorage !== false
        );
        setMemoryCache(endpointId, data, updatedAt);
        return data;
      })
      .finally(() => {
        inFlightRevalidations.delete(endpointId);
      });

    inFlightRevalidations.set(endpointId, request);
  }

  if (typeof onRevalidated === 'function') {
    request
      .then((data) => {
        if (data != null) {
          onRevalidated(data);
        }
      })
      .catch(() => {});
  }

  return request;
}

/**
 * Stale-while-revalidate:
 * 1. memory cache
 * 2. localStorage cache
 * 3. static /public/wp-cache/*.json
 * 4. WordPress remote
 */
export async function fetchWpCached(endpointId, options = {}) {
  const { onRevalidated } = options;
  const ep = getEndpoint(endpointId);
  const storageKey = `wp-cache-${endpointId}`;
  const revalidateMs =
    typeof ep.revalidateMs === 'number' ? ep.revalidateMs : DEFAULT_REVALIDATE_MS;

  if (ep.persistToStorage === false) {
    clearStoredData(storageKey);
  }

  const memoryEntry = getMemoryCache(endpointId);
  if (memoryEntry?.data != null) {
    if (shouldRevalidate(memoryEntry.updatedAt, revalidateMs)) {
      void queueRevalidation(endpointId, ep, storageKey, onRevalidated);
    }
    return memoryEntry.data;
  }

  if (ep.persistToStorage !== false) {
    const storedEntry = readStoredData(storageKey);
    if (storedEntry?.data != null) {
      const transformedStoredData = transformEndpointData(ep, storedEntry.data);
      setMemoryCache(endpointId, transformedStoredData, storedEntry.updatedAt);
      if (shouldRevalidate(storedEntry.updatedAt, revalidateMs)) {
        void queueRevalidation(endpointId, ep, storageKey, onRevalidated);
      }
      return transformedStoredData;
    }
  }

  if (ep.file) {
    try {
      const staticData = transformEndpointData(ep, await fetchLocalStaticCache(ep.file));
      const lastRemoteUpdate = readStoredTimestamp(storageKey);
      setMemoryCache(endpointId, staticData, lastRemoteUpdate);
      if (shouldRevalidate(lastRemoteUpdate, revalidateMs)) {
        void queueRevalidation(endpointId, ep, storageKey, onRevalidated);
      }
      return staticData;
    } catch {
      /* ignore static cache errors */
    }
  }

  const remoteData = await queueRevalidation(endpointId, ep, storageKey, onRevalidated)
    .catch(() => null);

  if (remoteData != null) {
    return remoteData;
  }

  throw new Error(`wpClient: no cache and remote failed for "${endpointId}"`);
}

export function getWpCachedSeed(endpointId) {
  return getSeedData(endpointId);
}

/**
 * Article par slug : liste complete en cache d'abord, puis revalidation REST (?slug=).
 */
export async function fetchPostBySlug(slug, options = {}) {
  const { onRevalidated } = options;
  if (!slug) return null;

  let fromList = null;
  try {
    const all = await fetchWpCached('posts');
    if (Array.isArray(all)) {
      fromList = all.find((p) => p.slug === slug) || null;
    }
  } catch {
    /* ignore */
  }

  const remotePromise = fetch(
    `${URL_Back}/wp-json/wp/v2/posts?slug=${encodeURIComponent(slug)}`
  )
    .then((r) => (r.ok ? r.json() : []))
    .then((arr) => (Array.isArray(arr) && arr[0] ? arr[0] : null))
    .then((post) => {
      if (post && typeof onRevalidated === 'function') {
        onRevalidated(post);
      }
      return post;
    })
    .catch(() => null);

  if (fromList) {
    void remotePromise;
    return fromList;
  }

  return await remotePromise;
}

/**
 * Article par id (liens internes dans le HTML) : cache posts puis REST.
 */
export async function fetchWpPostById(id) {
  if (id == null || id === '') return null;
  try {
    const all = await fetchWpCached('posts');
    if (Array.isArray(all)) {
      const hit = all.find((p) => String(p.id) === String(id));
      if (hit?.slug) return hit;
    }
  } catch {
    /* ignore */
  }
  try {
    const res = await fetch(`${URL_Back}/wp-json/wp/v2/posts/${id}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function formatWpImageUrl(url, options = {}) {
  const { preserveThumbnail = false } = options;

  if (!url || typeof url !== 'string') {
    return '';
  }

  if (!/\/wp-content\/uploads\//i.test(url)) {
    return url;
  }

  if (preserveThumbnail) {
    return url;
  }

  return normalizeWpImageUrl(url);
}

export function resolvePostImageUrl(post, imageField, options = {}) {
  const imageUrl = imageField ? post?.[imageField]?.[0]?.src : '';
  return formatWpImageUrl(imageUrl, options);
}

function extractRenderedText(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

export function getPortfolioLanguage(item) {
  const excerptText = extractRenderedText(item?.excerpt?.rendered).toLowerCase();

  if (excerptText === 'english') {
    return 'en';
  }

  if (excerptText === 'العربية') {
    return 'ar';
  }

  if (
    excerptText === 'french' ||
    excerptText === 'français' ||
    excerptText === 'francais'
  ) {
    return 'fr';
  }

  return 'fr';
}

export function filterPortfolioByLanguage(allPortfolios, currentLanguage) {
  if (!Array.isArray(allPortfolios)) {
    return [];
  }
  return allPortfolios.filter(
    (element) => getPortfolioLanguage(element) === currentLanguage
  );
}
